import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ApiError } from '@/shared/api/client';
import { useAuth } from '@/shared/auth/useAuth';
import { formatDateTime } from '@/shared/lib/format';
import { Button } from '@/shared/ui/Button';
import { ConfirmDialog } from '@/shared/ui/Modal';
import { useToast } from '@/shared/ui/useToast';

import { creditConsumption } from './api';

/**
 * Mensagens das recusas da Master, pelo codigo do servidor. O limite de consultas fica de
 * fora: a mensagem do servidor diz qual limite foi atingido e o uso (ADR 0098).
 */
const recusas: Record<string, string> = {
  CREDITO_CONSULTA_MASTER_EXIGE_BASICA:
    'A Master e a segunda etapa: faca antes a consulta basica do tomador.',
  CREDITO_CONSULTA_MASTER_DESABILITADA:
    'A consulta Master nao esta habilitada nos parametros de credito da empresa.',
  CREDITO_CONSULTA_PRODUTO_INDISPONIVEL:
    'A consulta Master nao esta disponivel nesta instalacao para este tomador.',
  CREDITO_CONSULTA_LIMITE_FINANCEIRO:
    'A empresa atingiu o limite financeiro mensal das consultas.',
  CREDITO_CONSULTA_PRECO_DESCONHECIDO:
    'O produto nao tem preco cadastrado e a empresa tem limite financeiro: a consulta nao sai.',
  CREDITO_CONSULTA_PAGA_SEM_PERMISSAO:
    'A politica da empresa exige permissao especifica para a consulta paga.',
  CREDITO_CONSULTA_EM_ANDAMENTO:
    'Ja existe uma consulta em andamento para este tomador.',
};

/**
 * Botao da segunda etapa da consulta: a Master da Vigidata, pedida pela pessoa, com a
 * confirmacao do custo (C-VGD).
 *
 * Fica na secao de analise de credito do tomador. A Master exige a basica valida, e a
 * Master valida e reaproveitada sem custo, com as regras reavaliadas na hora; quem decide
 * e o servidor, que tambem confere os limites e a politica de autorizacao. A pessoa sabe
 * antes do clique se vai haver consulta nova, e a tela se atualiza sozinha depois dele.
 */
export function MasterConsultationButton({
  policyholderId,
  masterValida,
}: {
  policyholderId: string;
  /** A consulta Master dentro da validade, quando ha. */
  masterValida?: { consulted_at: string; valid_until: string } | undefined;
}) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [confirmando, setConfirmando] = useState(false);

  const consultar = useMutation({
    mutationFn: () => creditConsumption.requestMaster(policyholderId, true),
    onSuccess: (consulta) => {
      toast.success(
        consulta.reused
          ? consulta.reanalyzed
            ? 'Consulta Master reaproveitada, sem nova cobranca. As regras foram reavaliadas agora.'
            : 'Consulta Master valida reaproveitada, sem nova cobranca.'
          : consulta.simulated
            ? 'Consulta Master registrada. O bureau configurado e ficticio: o resultado e SIMULADO.'
            : 'Consulta Master registrada. O retorno e as regras ja estao na tela.',
      );
      setConfirmando(false);
      void queryClient.invalidateQueries({
        queryKey: ['policyholder', policyholderId],
      });
    },
    onError: (erro: Error) => {
      setConfirmando(false);
      toast.error(
        erro instanceof ApiError ? (recusas[erro.code] ?? erro.message) : erro.message,
      );
    },
  });

  if (!can('credito.consulta.create')) return null;

  return (
    <>
      <Button size="sm" variant="secondary" onClick={() => setConfirmando(true)}>
        Consultar Master
      </Button>
      <ConfirmDialog
        open={confirmando}
        onClose={() => setConfirmando(false)}
        title={masterValida ? 'Consulta Master ja realizada' : 'Consultar a Master'}
        message={
          masterValida
            ? `Ja existe uma consulta Master de ${formatDateTime(masterValida.consulted_at)}, valida ate ${formatDateTime(masterValida.valid_until)}. Ela sera reaproveitada, sem nova cobranca, e as regras serao reavaliadas agora. Nenhum dado novo vem do fornecedor.`
            : 'A consulta Master e a segunda etapa e pode ser cobrada pelo fornecedor. Ela exige a consulta basica valida. O retorno e as regras aparecem aqui assim que ela terminar.'
        }
        confirmLabel={masterValida ? 'Reavaliar sem custo' : 'Consultar Master'}
        loading={consultar.isPending}
        onConfirm={() => consultar.mutate()}
      />
    </>
  );
}
